import { BaseSyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';

import useResendMessageLogMutation from 'components/domain/automation/messageLogs/useResendMessageLogMutation';
import useNotify from 'hooks/useNotify';
import { BaseErrorResponse } from 'models/_base';
import { getErrorMessage } from 'utils/axiosExceptionUtils';
import { FormContextParts } from 'components/forms/form/Form.types';
import { flushSync } from 'react-dom';
import { MessageLogViewModalFormValues } from './MessageLogViewModal.schema';

const useMessageLogViewModalFormSubmit = ({
  messageLogUid,
  closeModal,
}: {
  messageLogUid: string;
  closeModal: () => void;
}) => {
  const { t } = useTranslation();
  const { notifyError, notifySuccess } = useNotify();

  const onError = (error: BaseErrorResponse) => {
    notifyError(
      getErrorMessage({
        apiErrorCode: error.response?.data?.apiErrorCode,
        apiErrorMessage: error.response?.data?.apiErrorMessage ?? '',
        baseKey: 'pageAutomations.triggers.apiErrorCodes.',
        t,
      }),
    );
  };

  const { mutateAsync: resendMessageLog } = useResendMessageLogMutation();

  const onSubmit = async (
    formValues: MessageLogViewModalFormValues,
    _event: BaseSyntheticEvent | undefined,
    { reset }: FormContextParts<MessageLogViewModalFormValues>,
  ) => {
    const { templateContent_htmlContent, templateSubject } = formValues;

    try {
      await resendMessageLog(
        {
          templateContent: templateContent_htmlContent,
          templateTitle: templateSubject,
          messageLogUid,
        },
        {
          onSuccess: () => {
            // flushSync ensures the form is reset synchronously before
            // closeModal() triggers navigation. Without it, the dirty checker
            // would still see a dirty form during navigation and prompt the
            // user with an unsaved-changes confirmation.
            flushSync(() => reset(formValues, { keepDirty: false }));
            closeModal();
            notifySuccess(t('pageAutomations.messageLog.modal.success'));
          },
        },
      );
    } catch (error) {
      onError(error as BaseErrorResponse);
    }
  };

  return { onSubmit };
};

export default useMessageLogViewModalFormSubmit;
